import NumberFlow from "@number-flow/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  cn,
  isTruthy,
  parseDate,
  pluralize,
  queries,
  SCMThreat,
  useAuthConfig,
} from "@theprelude/core";
import { ThreatSelector } from "@/components/control-monitor/threat-selector";
import { createQuery } from "@/lib/create-query";
import {
  aggregateTechniqueSummaryQuery,
  useExceptedFilters,
} from "@/queries/control-monitor";
import { Card, CardHeader, ListItem } from "../sections";
import { SummaryStatusIcon } from "../summary-icon-bubbles";

export const RecentThreatsCard = () => {
  const recentThreats = useRecentThreats();

  return (
    <Card className="flex flex-col gap-4">
      <CardHeader>Recent Threats</CardHeader>
      <div className="flex flex-col">
        {recentThreats.map((threat) => (
          <ListItem
            key={threat.id}
            asChild
            className="flex items-center justify-between gap-2"
          >
            <Link to="/control-monitor" search={{ threat_id: threat.id }}>
              <div className="flex flex-1 items-center text-start">
                {threat.name}
              </div>
              <div
                className={cn(
                  "flex w-[120px] items-center justify-start gap-2 font-medium",
                  threat.issueCount > 0
                    ? "text-status-red"
                    : "text-status-green",
                )}
              >
                <SummaryStatusIcon
                  success={threat.issueCount === 0}
                  size="small"
                />
                <NumberFlow value={threat.issueCount} />{" "}
                {pluralize(threat.issueCount, "issue")}
              </div>
            </Link>
          </ListItem>
        ))}
      </div>
      <ThreatSelector />
    </Card>
  );
};

interface RecentThreat extends SCMThreat {
  issueCount: number;
}

const useRecentThreats = () => {
  const authConfig = useAuthConfig();
  const { showExcepted } = useExceptedFilters();

  const { data: threats } = useSuspenseQuery({
    ...queries.scm.threats(authConfig),
    select: (data) =>
      data
        .toSorted(
          (a, b) =>
            parseDate(b.created).getTime() - parseDate(a.created).getTime(),
        )
        .slice(0, 5),
  });

  const { data: issues } = createQuery({
    queries: threats.map((threat) =>
      aggregateTechniqueSummaryQuery(authConfig, {
        threatId: threat.id,
        showExcepted,
      }),
    ),
    queryFn: (...summaries) => {
      return summaries
        .map((summary, i): RecentThreat | undefined => {
          const threat = threats[i];

          if (!threat) {
            return undefined;
          }

          const controlFailureCount =
            summary.summary.endpoint_summary.categories.reduce(
              (sum, c) =>
                sum +
                ((showExcepted
                  ? c.control_failure_count
                  : c.excepted?.control_failure_count) ?? 0),
              0,
            );

          const misconfigurationCount = [
            ...summary.summary.endpoint_summary.categories,
            ...summary.summary.inbox_summary.categories,
            ...summary.summary.user_summary.categories,
          ].reduce((sum, c) => {
            const count = c.instances.reduce(
              (sum, i) =>
                sum +
                ((showExcepted
                  ? i.setting_misconfiguration_count
                  : i.excepted.setting_misconfiguration_count) ?? 0),
              0,
            );
            return sum + count;
          }, 0);

          const issueCount = controlFailureCount + misconfigurationCount;

          return { ...threat, issueCount };
        })
        .filter(isTruthy);
    },
  }).useSuspenseQuery();

  return issues;
};
